import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge, Button, Card, Checkbox, Collapse, Divider, Group, Loader, MultiSelect,
  Paper, SegmentedControl, Select, Stack, Text, Title,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconChevronDown, IconChevronUp, IconFilter, IconHelp, IconPlus, IconTrash } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import HttpClient from '../helpers/HttpClient';
import {
  AttendanceStatus, TrainingEvent, TrainingEventType, TrainingParticipant,
} from '../types/training-event.type';
import { Tag } from '../types/tag.type';
import { useNavigate } from 'react-router-dom';
import AttendanceChart, { AttendanceMetric, METRIC_LABELS } from '../components/AttendanceChart';
import dayjs from 'dayjs';
import { startTrainingListTour, startTrainingListTourOnFirstVisit } from '../helpers/trainingTour';

function minutesToTime(m: number): string {
  return `${Math.floor(m / 60).toString().padStart(2, '0')}:${(m % 60).toString().padStart(2, '0')}`;
}

const EVENT_TYPE_LABELS: Record<TrainingEventType, string> = {
  [TrainingEventType.TRAINING]: 'Training',
  [TrainingEventType.MATCH]: 'Spiel',
  [TrainingEventType.EVENT]: 'Veranstaltung',
};

const EVENT_TYPE_COLORS: Record<TrainingEventType, string> = {
  [TrainingEventType.TRAINING]: 'blue',
  [TrainingEventType.MATCH]: 'green',
  [TrainingEventType.EVENT]: 'violet',
};

interface EventStats {
  present: number;
  excused: number;
  unexcused: number;
  total: number;
  attendancePct: number;
}

function computeEventStats(participants: TrainingParticipant[]): EventStats {
  const present   = participants.filter((p) => p.attendanceStatus === AttendanceStatus.PRESENT).length;
  const excused   = participants.filter((p) => p.attendanceStatus === AttendanceStatus.EXCUSED).length;
  const unexcused = participants.filter((p) => p.attendanceStatus === AttendanceStatus.UNEXCUSED).length;
  const total = participants.length;
  return { present, excused, unexcused, total, attendancePct: total ? Math.round((present / total) * 100) : 0 };
}

function computePersonStats(participants: TrainingParticipant[], personId: number): EventStats | null {
  const mine = participants.filter((p) => p.person?.id === personId);
  if (mine.length === 0) return null;
  return computeEventStats(mine);
}

function sumStats(statsList: (EventStats | null)[]): EventStats {
  const valid = statsList.filter((s): s is EventStats => s !== null);
  const present   = valid.reduce((a, s) => a + s.present, 0);
  const excused   = valid.reduce((a, s) => a + s.excused, 0);
  const unexcused = valid.reduce((a, s) => a + s.unexcused, 0);
  const total     = valid.reduce((a, s) => a + s.total, 0);
  return { present, excused, unexcused, total, attendancePct: total ? Math.round((present / total) * 100) : 0 };
}

function StatsRow({ stats, personMode }: { stats: EventStats | null; personMode: boolean }) {
  if (!stats || stats.total === 0) {
    return <Text size="xs" c="dimmed">{personMode ? 'Nicht teilgenommen' : 'Keine Teilnehmer'}</Text>;
  }
  return (
    <Group gap={6} wrap="wrap">
      <Badge size="sm" color="green"  variant="light">✓ {stats.present}</Badge>
      <Badge size="sm" color="yellow" variant="light">~ {stats.excused}</Badge>
      <Badge size="sm" color="red"    variant="light">✗ {stats.unexcused}</Badge>
      <Text size="xs" c="dimmed">/ {stats.total} &nbsp;·&nbsp; {stats.attendancePct}%</Text>
    </Group>
  );
}

function AggregateStrip({ stats, personMode }: { stats: EventStats; personMode: boolean }) {
  return (
    <Paper p="sm" radius="md" withBorder>
      <Group justify="space-between" wrap="wrap" gap="xs">
        <Text size="sm" fw={600}>{personMode ? 'Persönliche Statistik' : 'Gesamtstatistik'}</Text>
        <Group gap="xs" wrap="wrap">
          <Badge size="md" color="green"  variant="light">Anwesend {stats.present}</Badge>
          <Badge size="md" color="yellow" variant="light">Entschuldigt {stats.excused}</Badge>
          <Badge size="md" color="red"    variant="light">Unentschuldigt {stats.unexcused}</Badge>
          <Badge size="md" color="gray"   variant="light">Gesamt {stats.total}</Badge>
          <Badge size="md" color="green"  variant="filled">{stats.attendancePct}% Anwesenheit</Badge>
        </Group>
      </Group>
    </Paper>
  );
}

const TRAINING_TAG_FILTER_KEY = 'training_tag_filter';

function TrainingEventList() {
  const navigate = useNavigate();

  const [events,  setEvents]  = useState<TrainingEvent[]>([]);
  const [tags,    setTags]    = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);

  // Person options accumulated from loaded events (assignedPerson + participants)
  const personOptionsMap = useRef<Map<string, string>>(new Map());
  const [personOptions, setPersonOptions] = useState<{ value: string; label: string }[]>([]);

  // Server-side filters
  const [dateFrom,          setDateFrom]          = useState<Date | null>(() => new Date(new Date().getFullYear(), new Date().getMonth(), 1));
  const [dateTo,            setDateTo]            = useState<Date | null>(() => new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));
  const [filterEventTypes,  setFilterEventTypes]  = useState<string[]>([]);
  const [assignedPersonId,  setAssignedPersonId]  = useState<string | null>(null);

  // Client-side filters
  const [filterTagIds,      setFilterTagIds]      = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(TRAINING_TAG_FILTER_KEY) ?? '[]'); } catch { return []; }
  });
  const [attendancePersonId, setAttendancePersonId] = useState<string | null>(null);

  // chart
  const [metric,       setMetric]       = useState<AttendanceMetric>('attendance');
  const [filtersOpen,  setFiltersOpen]  = useState(false);

  // bulk selection
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [deleting, setDeleting] = useState(false);

  const collectPersons = useCallback((evs: TrainingEvent[]) => {
    const map = personOptionsMap.current;
    evs.forEach((ev) => {
      if (ev.assignedPerson) {
        map.set(ev.assignedPerson.id.toString(), `${ev.assignedPerson.firstName} ${ev.assignedPerson.lastName}`);
      }
      ev.participants?.forEach((p) => {
        if (p.person) {
          map.set(p.person.id.toString(), `${p.person.firstName} ${p.person.lastName}`);
        }
      });
    });
    setPersonOptions(
      Array.from(map.entries())
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    );
  }, []);

  const fetchEvents = useCallback((
    from: Date | null,
    to: Date | null,
    types: string[],
    personId: string | null,
  ) => {
    setLoading(true);
    const qp: Record<string, string> = {};
    if (from) qp.dateFrom = dayjs(from).format('YYYY-MM-DD');
    if (to)   qp.dateTo   = dayjs(to).format('YYYY-MM-DD');
    if (types.length)  qp.eventTypes        = types.join(',');
    if (personId)      qp.assignedPersonId  = personId;

    new HttpClient<TrainingEvent>('/training-event').getAll(qp)
      .then((evs) => {
        setEvents(evs as TrainingEvent[]);
        collectPersons(evs as TrainingEvent[]);
      })
      .finally(() => setLoading(false));
  }, [collectPersons]);

  const toggleSelected = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`${selectedIds.size} Trainingseinheit(en) löschen?`)) return;
    setDeleting(true);
    const client = new HttpClient<TrainingEvent>('/training-event');
    const results = await Promise.allSettled([...selectedIds].map((id) => client.delete(id)));
    const failed = results.filter((r) => r.status === 'rejected').length;
    setDeleting(false);
    setSelectedIds(new Set());
    fetchEvents(dateFrom, dateTo, filterEventTypes, assignedPersonId);
    if (failed > 0) {
      notifications.show({ color: 'red', message: `${failed} von ${results.length} konnten nicht gelöscht werden.` });
    } else {
      notifications.show({ color: 'green', message: `${results.length} Trainingseinheit(en) gelöscht.` });
    }
  };

  // Fetch tags once on mount
  useEffect(() => {
    new HttpClient<Tag>('/tag').getAll().then((tgs) => setTags(tgs as Tag[]));
  }, []);

  // Start the tutorial once the list has rendered, so its event-card anchor exists.
  useEffect(() => {
    if (!loading) startTrainingListTourOnFirstVisit();
  }, [loading]);

  // Refetch events whenever server-side filters change
  useEffect(() => {
    fetchEvents(dateFrom, dateTo, filterEventTypes, assignedPersonId);
  }, [dateFrom, dateTo, filterEventTypes, assignedPersonId, fetchEvents]);

  // Tag filter is applied client-side on the already-fetched (small) result set
  const filteredEvents = useMemo(() => {
    if (!filterTagIds.length) return events;
    return events.filter((ev) => ev.tags?.some((t) => filterTagIds.includes(t.id.toString())));
  }, [events, filterTagIds]);

  const attendancePersonIdNum = attendancePersonId ? Number(attendancePersonId) : null;

  const perEventStats = useMemo(() =>
    filteredEvents.map((ev) => {
      const pool = ev.participants ?? [];
      return attendancePersonIdNum
        ? computePersonStats(pool, attendancePersonIdNum)
        : computeEventStats(pool);
    }),
    [filteredEvents, attendancePersonIdNum],
  );

  const aggregateStats = useMemo(() => sumStats(perEventStats), [perEventStats]);

  const tagOptions  = tags.map((t) => ({ value: t.id.toString(), label: t.name }));
  const typeOptions = Object.values(TrainingEventType).map((v) => ({ value: v, label: EVENT_TYPE_LABELS[v] }));

  const hasFilters = !!(dateFrom || dateTo || filterTagIds.length || filterEventTypes.length || assignedPersonId || attendancePersonId);

  return (
    <Stack p="md" gap="md">
      {/* Header */}
      <Group justify="space-between" align="center">
        <Title order={2}>Trainingseinheiten</Title>
        <Group gap="xs">
          <Button variant="subtle" size="md" leftSection={<IconHelp size={16} />} onClick={startTrainingListTour}>
            Anleitung
          </Button>
          <Button
            data-tour="filter"
            variant={hasFilters ? 'light' : 'subtle'}
            color={hasFilters ? 'blue' : 'gray'}
            leftSection={<IconFilter size={16} />}
            rightSection={filtersOpen ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
            size="md"
            onClick={() => setFiltersOpen((o) => !o)}
          >
            Filter{hasFilters ? ' (aktiv)' : ''}
          </Button>
          <Button data-tour="new-series" variant="light" size="md" onClick={() => navigate('/training/events/series/create')}>
            Serie erstellen
          </Button>
          <Button data-tour="new-event" leftSection={<IconPlus size={16} />} size="md" onClick={() => navigate('/training/events/create')}>
            Neu
          </Button>
        </Group>
      </Group>

      {/* Filter panel */}
      <Collapse in={filtersOpen}>
        <Paper p="md" radius="md" withBorder>
          <Stack gap="sm">
            <Group grow wrap="wrap" gap="sm">
              <DatePickerInput
                label="Von"
                value={dateFrom}
                onChange={(v) => setDateFrom(v ? new Date(v) : null)}
                clearable
                valueFormat="DD.MM.YYYY"
                size="sm"
              />
              <DatePickerInput
                label="Bis"
                value={dateTo}
                onChange={(v) => setDateTo(v ? new Date(v) : null)}
                clearable
                valueFormat="DD.MM.YYYY"
                size="sm"
              />
            </Group>
            <Group grow wrap="wrap" gap="sm">
              <MultiSelect
                label="Tags"
                data={tagOptions}
                value={filterTagIds}
                onChange={(v) => { setFilterTagIds(v); localStorage.setItem(TRAINING_TAG_FILTER_KEY, JSON.stringify(v)); }}
                searchable
                clearable
                size="sm"
              />
              <MultiSelect
                label="Typ"
                data={typeOptions}
                value={filterEventTypes}
                onChange={setFilterEventTypes}
                clearable
                size="sm"
              />
            </Group>
            <Group grow wrap="wrap" gap="sm">
              <Select
                label="Verantwortlich"
                data={personOptions}
                value={assignedPersonId}
                onChange={setAssignedPersonId}
                searchable
                clearable
                size="sm"
              />
              <Select
                label="Anwesenheit von Person"
                data={personOptions}
                value={attendancePersonId}
                onChange={setAttendancePersonId}
                searchable
                clearable
                size="sm"
                description="Statistik auf diese Person einschränken"
              />
            </Group>
          </Stack>
        </Paper>
      </Collapse>

      {/* Aggregate stats + chart */}
      {filteredEvents.length > 0 && (
        <Stack gap="md" data-tour="stats">
          <AggregateStrip stats={aggregateStats} personMode={!!attendancePersonId} />
          <Paper p="md" radius="md" withBorder>
            <Group justify="space-between" align="center" mb="sm" wrap="wrap" gap="xs">
              <Text size="sm" fw={600}>Verlauf</Text>
              <SegmentedControl
                size="xs"
                value={metric}
                onChange={(v) => setMetric(v as AttendanceMetric)}
                data={Object.entries(METRIC_LABELS).map(([value, label]) => ({ value, label }))}
              />
            </Group>
            <AttendanceChart events={filteredEvents} metric={metric} personId={attendancePersonIdNum} />
          </Paper>
        </Stack>
      )}

      <Divider />

      {selectedIds.size > 0 && (
        <Paper p="sm" radius="md" withBorder>
          <Group justify="space-between" wrap="wrap" gap="xs">
            <Text size="sm" fw={600}>{selectedIds.size} ausgewählt</Text>
            <Group gap="xs">
              <Button variant="subtle" size="xs" onClick={() => setSelectedIds(new Set())}>
                Auswahl aufheben
              </Button>
              <Button
                color="red"
                size="xs"
                leftSection={<IconTrash size={14} />}
                loading={deleting}
                onClick={handleBulkDelete}
              >
                Löschen
              </Button>
            </Group>
          </Group>
        </Paper>
      )}

      {loading && <Loader mt="xl" />}

      {!loading && filteredEvents.length === 0 && (
        <Text c="dimmed" ta="center" py="xl">Keine Trainingseinheiten gefunden</Text>
      )}

      {!loading && filteredEvents.map((event, idx) => (
        <Card
          key={event.id}
          data-tour={idx === 0 ? 'event-card' : undefined}
          shadow="sm"
          padding="md"
          radius="md"
          withBorder
          style={{ cursor: 'pointer', opacity: event.isCancelled ? 0.6 : 1 }}
          onClick={() => navigate(`/training/events/edit/${event.id}`)}
        >
          <Group justify="space-between" mb="xs" wrap="wrap" gap="xs">
            <Group gap="xs" wrap="wrap">
              <Checkbox
                checked={selectedIds.has(event.id)}
                onChange={() => toggleSelected(event.id)}
                onClick={(e) => e.stopPropagation()}
                aria-label="Auswählen"
              />
              <Badge color={EVENT_TYPE_COLORS[event.eventType] ?? 'gray'} variant="light" size="md">
                {EVENT_TYPE_LABELS[event.eventType] ?? event.eventType}
              </Badge>
              {(event.tags ?? []).map((tag) => (
                <Badge key={tag.id} color={tag.color || 'blue'} variant="filled" size="md">{tag.name}</Badge>
              ))}
            </Group>
            <Group gap="xs">
              {event.isCancelled && <Badge color="red" variant="filled" size="md">Storniert</Badge>}
              <Badge
                color={event.status === 'graded' ? 'gray' : 'green'}
                variant="light"
                size="md"
              >
                {event.status === 'graded' ? 'Bewertet' : 'Offen'}
              </Badge>
            </Group>
          </Group>

          <Text fw={600} fz="lg">
            {new Date(event.date).toLocaleDateString('de-AT', {
              weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric',
            })}
          </Text>
          <Text size="sm" c="dimmed">
            {minutesToTime(event.startTime)} Uhr &nbsp;·&nbsp; {event.duration} Min.
          </Text>
          <Text size="sm" c="dimmed">
            {event.assignedPerson?.firstName} {event.assignedPerson?.lastName}
          </Text>
          {event.cancelReason && (
            <Text size="xs" c="red" mt={4}>Stornierungsgrund: {event.cancelReason}</Text>
          )}

          <Divider my="xs" />
          <StatsRow stats={perEventStats[idx]} personMode={!!attendancePersonId} />
        </Card>
      ))}
    </Stack>
  );
}

export default TrainingEventList;
